import { FC, useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Form, Image, Input, message, Modal, Skeleton, Space, Tooltip } from 'antd'
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { Combo, ImageUpload } from '../../../core/component'
import { resolvePathFile } from '../../../core/until'
import { GetVendorsForLookup } from '../../contact/graphql/contact.queries.graphql'
import { GetItemCategoriesForLookup } from '../../inventory/graphql/item-category.graphql'
import { Contact } from '../../contact/type/contact'
import { ItemCategory, ItemCategoryLevel } from '../../inventory/type'
import { ContactFindRequest } from '../../contact/type/contact.query'
import { ItemCategoryFindRequest } from '../../inventory/type'
import { QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import {
  CreateCarouselSlide,
  UpdateCarouselSlide,
} from '../graphql/carousel.graphql'
import {
  CarouselGroup,
  CarouselMainSlide,
  CarouselSubSlide,
} from '../type/carousel'
import { CarouselSubSlideSection } from './CarouselSubSlideSection'

type Props = {
  group: CarouselGroup
  slide?: CarouselMainSlide
  onClose: (saved?: boolean) => void
}

type FormValues = {
  entityId: number
  title: string
  subtitle?: string
  mainImage: string
  subSlides: CarouselSubSlide[]
}

export const CarouselMainSlideForm: FC<Props> = ({ group, slide, onClose }) => {
  const [form] = Form.useForm<FormValues>()
  const [messageApi, contextHolder] = message.useMessage()
  const [mainImageUploading, setMainImageUploading] = useState(false)

  const [vendorsFind] = useLazyQuery<
    QueryPageableResponse<Contact, 'contactsFind'>,
    QueryPageableRequest<ContactFindRequest>
  >(GetVendorsForLookup)

  const [categoriesFind] = useLazyQuery<
    QueryPageableResponse<ItemCategory, 'itemCategoriesFind'>,
    QueryPageableRequest<ItemCategoryFindRequest>
  >(GetItemCategoriesForLookup)

  const [create, { loading: creating }] = useMutation(CreateCarouselSlide, {
    onCompleted: () => {
      messageApi.success(translate('carousel', 'save_success_message'))
      onClose(true)
    },
    onError: (err) => messageApi.error(err.message),
  })

  const [update, { loading: updating }] = useMutation(UpdateCarouselSlide, {
    onCompleted: () => {
      messageApi.success(translate('carousel', 'update_success_message'))
      onClose(true)
    },
    onError: (err) => messageApi.error(err.message),
  })

  useEffect(() => {
    if (slide) {
      form.setFieldsValue({
        entityId: slide.entityId,
        title: slide.title,
        subtitle: slide.subtitle,
        mainImage: slide.mainImage,
        subSlides: slide.subSlides.map((s) => ({
          id: s.id,
          sku: s.sku,
          productId: s.productId,
          title: s.title,
          subtitle: s.subtitle,
          productImage: s.productImage,
          displayOrder: s.displayOrder,
        })),
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ subSlides: [] })
    }
  }, [slide, form])

  const handleFinish = (values: FormValues) => {
    const subSlides = (values.subSlides || []).map(
      ({ id, slug, ...rest }, index) => ({
        sku: rest.sku,
        productId: rest.productId,
        title: rest.title,
        subtitle: rest.subtitle ?? '',
        productImage: rest.productImage,
        displayOrder: index,
      }),
    )

    if (slide) {
      update({
        variables: {
          input: {
            id: slide.id,
            entityId: values.entityId,
            title: values.title,
            subtitle: values.subtitle,
            mainImage: values.mainImage,
            subSlides,
          },
        },
      })
    } else {
      create({
        variables: {
          input: {
            group,
            entityId: values.entityId,
            title: values.title,
            subtitle: values.subtitle,
            mainImage: values.mainImage,
            subSlides,
          },
        },
      })
    }
  }

  const handleEntitySelect = (_: number, entity: Contact | ItemCategory) => {
    if (group === CarouselGroup.Watch) {
      const vendor = entity as Contact
      form.setFieldValue('title', vendor.designer ?? vendor.company ?? vendor.contactId ?? '')
    } else {
      const category = entity as ItemCategory
      form.setFieldValue('title', category.title ?? '')
    }
  }

  const handleImageUploaded = (filename: string) => {
    form.setFieldValue('mainImage', filename)
  }

  const mainImage = Form.useWatch('mainImage', form)

  const isWatch = group === CarouselGroup.Watch

  return (
    <Modal
      open
      onCancel={() => onClose()}
      title={slide ? translate('edit', 'slide') : translate('new', 'slide')}
      onOk={form.submit}
      okButtonProps={{
        loading: creating || updating,
        icon: <SaveOutlined />,
      }}
      width="70%"
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          name="entityId"
          label={isWatch ? translate('select_brand') : translate('select_category')}
          rules={[
            {
              required: true,
              message: translate('missing_field_message', isWatch ? 'brand' : 'category'),
            },
          ]}
        >
          {isWatch ? (
            <Combo<Contact, number>
              style={{ width: '100%' }}
              placeholder={translate('select_brand')}
              onChange={(val, item) => {
                if (item && !Array.isArray(item)) {
                  form.setFieldValue('entityId', val)
                  handleEntitySelect(val as number, item)
                }
              }}
              fetch={async ({ request, search, value: lookupId }) => {
                const { data } = await vendorsFind({
                  variables: {
                    request: {
                      isVendor: true,
                      ...(!lookupId ? { hasDesigner: true } : {}),
                      take: request?.take ?? 20,
                      skip: request?.skip ?? 0,
                      ...(lookupId ? { id: lookupId } : {}),
                      ...(search ? { designer: search } : {}),
                    },
                  },
                })
                const list = data?.contactsFind.data ?? []
                return list.map((v) => ({
                  value: v.id,
                  display: v.designer || v.company || `#${v.id}`,
                  item: v,
                }))
              }}
            />
          ) : (
            <Combo<ItemCategory, number>
              style={{ width: '100%' }}
              placeholder={translate('select_category')}
              onChange={(val, item) => {
                if (item && !Array.isArray(item)) {
                  form.setFieldValue('entityId', val)
                  handleEntitySelect(val as number, item)
                }
              }}
              fetch={async ({ request, search, value: lookupId }) => {
                const { data } = await categoriesFind({
                  variables: {
                    request: {
                      take: request?.take ?? 20,
                      skip: request?.skip ?? 0,
                      level: ItemCategoryLevel.Category,
                      ...(lookupId ? { id: lookupId } : {}),
                      ...(search ? { title: search } : {}),
                    },
                  },
                })
                const list = data?.itemCategoriesFind.data ?? []
                return list.map((c) => ({
                  value: c.id,
                  display: c.title ?? `#${c.id}`,
                  item: c,
                }))
              }}
            />
          )}
        </Form.Item>

        <Form.Item
          name="title"
          label={translate('title')}
          rules={[{ required: true, message: translate('missing_field_message', 'title') }]}
        >
          <Input placeholder={translate('title')} />
        </Form.Item>

        <Form.Item name="subtitle" label={translate('subtitle')}>
          <Input placeholder={translate('subtitle')} />
        </Form.Item>

        <Form.Item
          name="mainImage"
          label={
            <Space size={4}>
              {translate('main_image')}
              <Tooltip title={translate('main_image_tooltip')}>
                <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: translate('missing_field_message', 'main_image') }]}
        >
          <Input type="hidden" />
        </Form.Item>

        <Space align="start" style={{ marginBottom: 16 }}>
          {mainImageUploading ? (
            <Skeleton.Image active style={{ width: 160, height: 100 }} />
          ) : (
            mainImage && (
              <Image
                width={160}
                height={100}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                src={resolvePathFile(mainImage)}
                preview={false}
              />
            )
          )}
          <ImageUpload
            onUploaded={handleImageUploaded}
            onUploadingChange={setMainImageUploading}
            label={`${translate('upload')} ${translate('main_image')}`}
          />
        </Space>

        <Form.Item
          name="subSlides"
          rules={[
            {
              validator: (_, val) => {
                if (!val || val.length !== 3) {
                  return Promise.reject(translate('exactly_three_sub_slides'))
                }
                const issues: string[] = []
                val.forEach((s: CarouselSubSlide, i: number) => {
                  if (!s.productId) {
                    issues.push(
                      `${translate('slide')} #${i + 1}: ${translate('missing_field_message', 'sku')}`,
                    )
                  }
                  if (!s.productImage) {
                    issues.push(
                      `${translate('slide')} #${i + 1}: ${translate('missing_field_message', 'product_image')}`,
                    )
                  }
                })
                if (issues.length) {
                  return Promise.reject(issues.join('; '))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <CarouselSubSlideSection group={group} />
        </Form.Item>
      </Form>
      {contextHolder}
    </Modal>
  )
}
